import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const OPTIMISTIC_CART_COUNT_MAX_AGE = 8000;

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {boolean} */
  #animationPending = false;

  /** @type {number} */
  get currentCartCount() {
    const value = this.dataset.cartCount ?? this.refs.cartBubbleCount.textContent ?? '0';
    const count = parseInt(value, 10);
    return Number.isFinite(count) ? count : 0;
  }

  set currentCartCount(value) {
    this.dataset.cartCount = String(value);
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    // A destination page can be rendered before a just-submitted cart mutation
    // reaches Shopify. Keep the recent optimistic count visible until the cart
    // drawer is next opened and refreshed from the server.
    this.#restoreRecentOptimisticCartCount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';
    const phase = event.detail.data?.phase;

    // The pending event already applied the optimistic delta. Confirmation only
    // settles the operation; the cart-section refresh supplies the exact count.
    if (comingFromProductForm && event.detail.data?.optimistic && phase !== 'pending') return;
    if (comingFromProductForm && event.detail.data?.didError) return;

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   * @param {boolean} [persist] - Whether to replace the persisted count.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true, persist = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count
    const nextCartCount = Math.max(0, comingFromProductForm ? this.currentCartCount + itemCount : itemCount);

    this.refs.cartBubbleCount.classList.toggle('hidden', nextCartCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', nextCartCount === 0);

    this.currentCartCount = nextCartCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', nextCartCount > 0);

    if (persist) {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(this.currentCartCount),
          timestamp: Date.now(),
          optimistic: comingFromProductForm,
        })
      );
    }

    if (!animate || itemCount === 0 || this.#animationPending) return;
    this.#animationPending = true;

    try {
      // Ensure element is visible before starting animation
      await new Promise((resolve) => requestAnimationFrame(resolve));
      this.refs.cartBubble.classList.add('cart-bubble--animating');
      await onAnimationEnd(this.refs.cartBubbleText);
    } finally {
      this.refs.cartBubble.classList.remove('cart-bubble--animating');
      this.#animationPending = false;
    }
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };

  #restoreRecentOptimisticCartCount() {
    let storedCount;
    try {
      storedCount = JSON.parse(sessionStorage.getItem('cart-count') || 'null');
    } catch (_) {
      return;
    }

    const count = Number.parseInt(storedCount?.value, 10);
    const age = Date.now() - Number(storedCount?.timestamp);
    if (
      !storedCount?.optimistic ||
      !Number.isFinite(count) ||
      age < 0 ||
      age > OPTIMISTIC_CART_COUNT_MAX_AGE ||
      count <= this.currentCartCount
    ) {
      return;
    }

    // Do not write this restored value back as an authoritative server value.
    void this.renderCartBubble(count, false, false, false);
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
